import { useCallback, useMemo } from 'react';
import { Input, PropsOf } from '@chakra-ui/react';

export type UseNumberInputProps = {
  onChange?: (value: string) => void;
  isValidCharacter?: (value: string) => boolean;
};

const isValidCharacterDefault = (character: string, index: number) => {
  if (index === 0) {
    return /^[0-9+-]$/.test(character);
  }
  return /^[0-9.]$/.test(character);
};

const isValidKeyboardEvent = (
  event: React.KeyboardEvent<HTMLInputElement>,
  isValidCharacter: (key: string, index: number) => boolean
) => {
  if (event.key == null || event.ctrlKey || event.altKey || event.metaKey || event.key.length !== 1) {
    return true;
  }
  return isValidCharacter(event.key, event.currentTarget.selectionStart ?? event.currentTarget.value.length);
};

export const useNumberInput = (props: UseNumberInputProps) => {
  const { isValidCharacter: isValidCharacterProp, onChange: onChangeProp } = props;

  const isValidCharacter = isValidCharacterProp || isValidCharacterDefault;

  const sanitize = useCallback(
    (value: string) => {
      return value
        .split('')
        .filter((char, i) => isValidCharacter(char, i))
        .join('');
    },
    [isValidCharacter]
  );

  const onKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (!isValidKeyboardEvent(e, isValidCharacter)) {
        e.preventDefault();
      }
    },
    [isValidCharacter]
  );

  const onChange = useCallback(
    (e: React.FormEvent<HTMLInputElement>) => {
      const value = sanitize(e.currentTarget.value);
      if (onChangeProp) {
        onChangeProp(value);
      }
    },
    [onChangeProp, sanitize]
  );

  const inputProps = useMemo(
    (): PropsOf<typeof Input> => ({
      type: 'text',
      onChange,
      onKeyDown,
    }),
    [onChange, onKeyDown]
  );

  return inputProps;
};
